
import { User, Session } from '../types';
import { loginWithEmailPassword, loginWithProvider as loginWithProviderAuth } from './authOperations';
import { supabase } from '@/integrations/supabase/client';

export async function performLogin(email: string, password: string) {
  console.log('🔐 Starting login process for:', email);
  const loginStart = Date.now();
  
  // Validate and sanitize email input
  const sanitizedEmail = email.toLowerCase().trim();
  if (!sanitizedEmail || !sanitizedEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
    throw new Error('Invalid email format');
  }
  
  // Basic password validation for login - only check that it's provided
  if (!password || password.length === 0) {
    throw new Error('Password is required');
  }
  
  // Check for rate limiting
  const { data: rateLimitOk, error: rateLimitError } = await supabase.rpc('check_rate_limit', {
    user_uuid: '00000000-0000-0000-0000-000000000000', // Anonymous for login attempts
    action_name: 'login_attempt',
    max_attempts: 5,
    window_minutes: 15
  });
  
  if (rateLimitError || !rateLimitOk) {
    throw new Error('Too many login attempts. Please try again later.');
  }
  
  const { user, session } = await loginWithEmailPassword(sanitizedEmail, password);
  console.log('✅ Auth login completed in:', Date.now() - loginStart, 'ms');
  
  if (user && session) {
    const enhancedUser: User = {
      ...user,
      subscriptionTier: (user as any)?.subscription_tier || 'free',
      profileImage: (user as any)?.profile_image || null,
      provider: ((user.app_metadata?.provider) || 'email') as 'email' | 'google' | 'apple',
    };
    
    console.log('👤 Setting user and session...');
    
    // Check if user has MFA factors enrolled - this will determine if MFA challenge is needed
    const { data: factors, error: mfaError } = await supabase
      .from('mfa_factors')
      .select('*')
      .eq('user_id', user.id)
      .eq('status', 'verified');
    
    if (mfaError) {
      console.warn('Failed to check MFA factors:', mfaError);
    }
    
    // If user has verified MFA factors, the challenge will be handled by the auth context
    if (factors && factors.length > 0) {
      console.log('🔐 User has MFA enabled, challenge required');
    }
    
    console.log('✅ Login process completed successfully');
    return { user: enhancedUser, session, weakPassword: { isWeak: false } };
  }
  
  throw new Error('Login failed: No user or session returned');
}

export async function performProviderLogin(provider: string) {
  await loginWithProviderAuth(provider as 'google' | 'apple');
}
